<template>
  <div class="reporting-main-container--call-logs">
    <ReportingCallLogsSettings
      class="reporting-settings-container m-b-10"
      @refreshCallLogsData="handleCallLogsRefresh"
      @onStartTour="userTriggerStartTour"
      @dateRangeChange="handleDateRangeChange"
    />

    <div
      class="reporting-call-logs-data"
    >
      <!-- TODO: Move the header into the Histogram component -->
      <div class="reporting-call-logs-header  m-b-15">
        <div class="reporting-call-logs-header__title">
          Timeline
        </div>

        <div class="reporting-call-logs-header__right">
          <button
            v-if="histogramActions.length"
            class="btn m-r-10"
            @click="zoomOutHistogram"
          >
            <i class="fa fa-search-minus" />
            Zoom out
          </button>

          <SearchableDropdown
            v-if="sectionsVisibility['timeline']"
            v-model="timeGroupBy"
            class="call-logs__time-group-dropdown input-md m-r-15"
            :config="timeGroupDropdownOptions"
            placeholder="Interval"
            rules="required"
          />
        </div>

        <a
          href="javascript:;"
          class="btn btn-list-function"
          @click="toggleSection('timeline')"
        >
          <i
            class="typcn"
            :class="{'typcn-arrow-sorted-up': sectionsVisibility['timeline'],
                     'typcn-arrow-sorted-down': !sectionsVisibility['timeline']}"
          />
        </a>
      </div>

      <HighchartsCallHistogram
        v-if="sectionsVisibility['timeline']"
        :data="histogramData"
        :moment-timezone-id="activeCallLogTranslatedTimeZoneId"
        :group-time-interval="groupTimeInterval"
        :computed-time-group-interval="histogramTimeGroupInterval"
        :show-loader="showHistogramLoader"
        :time-range="activeCallLogTimeRange"
        @onRangeSelection="handleChartRangeSelection"
      />

      <!-- TODO: Move the header into the Quick Insights component -->
      <div class="reporting-call-logs-header m-b-15">
        <div class="reporting-call-logs-header__title">
          Summary
        </div>

        <div class="reporting-call-logs-header__right">
          <button
            v-if="canExportReport"
            class="btn btn-sm m-r-15 export-summary-btn"
            :disabled="disableQuickInsightsExport"
            @click="exportQuickInsightsReport"
          >
            {{ $t('EXPORT_CSV') }}
          </button>

          <a
            href="javascript:;"
            class="btn btn-list-function"
            @click="toggleSection('quickInsights')"
          >
            <i
              class="typcn"
              :class="{'typcn-arrow-sorted-up': sectionsVisibility['quickInsights'],
                       'typcn-arrow-sorted-down': !sectionsVisibility['quickInsights']}"
            />
          </a>
        </div>
      </div>

      <ReportingCallLogsQuickInsights
        v-if="sectionsVisibility['quickInsights']"
        class="m-b-20"
        :col-defs-loaded-from-api="insightsColDefsLoadedFromAPI"
        :column-defs="quickInsightsColumns"
        :row-data="quickFilterData"
        :tab-groupings="quickFilterInsightsTabs"
        :refresh-grid-data="refreshGridData"
        :column-state="quickFilterInsightsColumnState"
        :show-loader="quickFilterInsightsLoader"
        :tag-group-bys="tagGroupBys"
        :hide-tags="hideTags"
        :select-tab-grouping="quickInsightsTabGrouping"
        :role="role"
        :export-quick-insights-trigger="exportQuickInsightsTrigger"
        @loadData="loadQuickFilterInsightsData"
        @filterByColumn="handleFilterByColumn"
        @filterByDate="handleFilterByDate"
        @changeTabGrouping="onQuickInsightsTabChange"
        @gridStateChange="onCallLogSummaryGridStateChange"
      />

      <ReportingCallLogsGrid
        v-if="sectionsVisibility['callLog']" 
        ref="reportingCallLogsGrid"
        class="m-t-15"
        :call-logs-data="quickFilterData"
        :col-defs-loaded-from-api="callLogsColDefsLoadedFromAPI"
        :column-defs="callLogColumns"
        :grid-state="gridState"
        :show-call-log-action-loader-status="showCallLogActionLoaderStatus"
        :call-log-action-form-visibility-status="callLogActionFormVisibilityStatus"
        :campaigns="campaigns"
        :refresh-grid-data="refreshGridData"
        :role="role"
        :is-admin="isReportingAdmin"
        :is-read-only-report-user="isReadOnlyReportUser"
        :is-buyer="isBuyer"
        :recent-call-tags="recentCallTags"
        :show-audio-player-for-call="showAudioPlayerForCall"
        :call-log-details-mapping="callLogDetailsMapping"
        :blocked-numbers-hash-map="blockedNumbersHashMap"
        :show-bulk-action-loader="showBulkActionLoader"
        :hide-bulk-action-form="triggerBulkActionHide"
        :time-zone-id="activeCallLogTranslatedTimeZoneId"
        :can-dispute-conversions="canDisputeConversions"
        :dispute-call-status="disputeCallStatus"
        :can-export-report="canExportReport"
        :open-columns-tool-panel="openGridColumnToolPanel"
        :open-tour-call-details="openTourCallDetails"
        :show-export-loader="showExportLoader"
        :pending-bulk-call-adjustment-by-logs="pendingBulkAdjustmentByLogs"
        @getCallDetails="getCallDetailsFromAPI"
        @showAudioPlayer="showAudioPlayer"
        @stopAudioPlayer="stopAudioPlayer"
        @gridStateChange="onGridStateChange"
        @blockNumber="onBlockNumber"
        @blockMultipleNumbers="blockMultipleNumbers"
        @unblockNumber="onUnblockNumber"
        @annotateMultipleCalls="annotateMultipleCalls"
        @annotateCall="annotateCall"
        @callPaymentDispute="callPaymentDispute"
        @adjustMultipleCalls="multipleCallPaymentDispute"
        @adjustMultipleCallsByLogs="multipleCallPaymentDisputeByLogs"
        @transcribeMultipleCalls="multipleCallTranscription"
        @transcribeMultipleCallsByLogs="multipleCallTranscriptionByLogs"
        @onGridDataLoaded="onGridDataLoaded"
        @changeCallLogActionFormVisibilityStatus="setCallLogActionFormVisibilityStatus"
        @filterByColumn="handleFilterByColumn"
        @resetColumnState="onColumnReset"
        @exportReport="handleReportExport"
        @exportReportJSONRequestBody="handleExportReportJSONRequestBody"
      />
    </div>

    <v-tour
      name="callLogsTour"
      :steps="steps"
      :callbacks="tourCallbacks"
    >
      <template slot-scope="tour">
        <transition name="fade">
          <v-step
            v-for="(step, index) of tour.steps"
            v-if="tour.currentStep === index"
            :key="index"
            :step="step"
            :previous-step="tour.previousStep"
            :next-step="tour.nextStep"
            :stop="tour.stop"
            :is-first="tour.isFirst"
            :is-last="tour.isLast"
            :labels="tour.labels"
            :highlight="step.highlight"
          >
            <div
              slot="header"
              class="v-step__header"
            >
              <span />
              <span v-html="step.header ? step.header.title : ''" />
              <button @click.prevent="dontShowTourAgain(tour.currentStep)">
                <i class="fa fa-times" />
              </button>
            </div>
            <div slot="actions">
              <button
                v-if="!tour.isFirst"
                v-t="'TOUR_PREVIOUS'"
                class="btn btn-default btn-default--vue-tour btn-sm m-r-5"
                @click.prevent="tour.previousStep"
              />
              <button
                v-if="!tour.isLast"
                v-t="'TOUR_NEXT'"
                class="btn btn-primary btn-sm"
                @click.prevent="tour.nextStep"
              />
              <button
                v-if="tour.isLast"
                v-t="'TOUR_FINISH'"
                class="btn btn-primary btn-sm"
                @click.prevent="tour.finish"
              />
            </div>
          </v-step>
        </transition>
      </template>
    </v-tour>
    <AudioPlayer 
      v-if="audioInfo" 
      ref="audioPlayer" 
      :audio-info="audioInfo"
      @hidePlayer="hidePlayer"
      @viewTranscription="viewTranscription"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import moment from 'moment';
import Vue from 'vue';

import { isMobile } from '@/core/utilities';
import { insightsWithoutGroupingColumnState } from '@/modules/reporting/insightsColumnState';
import { ngVueEventBus, EVENT_STOP_PLAYER, EVENT_START_PLAYER } from '@/angularBridgeCode/utilities/ngVueEventBus';
import messages from './locales';
import sharedMessages from '@/core/locales';

import ReportingCallLogsSettings from './ReportingCallLogsSettings';
import HighchartsCallHistogram from './HighchartsCallHistogram';
import ReportingCallLogsGrid from './ReportingCallLogsGrid';
import ReportingCallLogsQuickInsights from './ReportingCallLogsQuickInsights';
import { CALLLOG_REPORT_TYPE } from '../../store/state';
import { isDefaultReportId } from '../../store/actions';
import { utilities, reportingService, userSettingsService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { findTimeInterval, timeGroupDropdownOptions, HISTOGRAM_POINTS_LIMIT } from '@/data/getDateRanges';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import AudioPlayer from '@/core/components/AudioPlayer/AudioPlayer';
import getCallLogsTourSteps from './calllogs-tour-steps';

const quickFilterInsightsTabsColumnIds = [
  'campaignName',
  'publisherName',
  'targetName',
  'buyer',
  'buyerTargetName',
  'number',
  'numberPoolName',
  'datesByYearMonthDay',
  'isDuplicate'
];

function getFiltersFromKeys(filterKeys, filterValues, availableFilters, existingFilterCriteria) {
  const allFilters = [];
  filterKeys.forEach((filterKey, idx) => {
    if (!filterValues[idx]) {
      return;
    }

    const isTag = filterKey.indexOf('tag:') > -1;
    const filter = availableFilters.find(col => (isTag ? `tag:${col.tagType}:${col.tagName}` === filterKey : col.fieldName === filterKey));

    if (filter
        && !reportingService.checkIfFieldExistsInCriteria(existingFilterCriteria, `${filter.tagType}:${filter.fieldName}`)) {

      allFilters.push({
        column: filterKey,
        value: filterValues[idx],
        filterGroupName: filter.tagType,
        type: filter.fieldType,
        isTag: filter.isTag,
        originalTag: filter.isTag ? { tagType: filter.tagType, tagName: filter.tagName } : null
      });
    }
  });

  return allFilters;
}

export default {
  name: 'ReportingCallLogs',
  components: {
    ReportingCallLogsSettings,
    HighchartsCallHistogram,
    ReportingCallLogsGrid,
    ReportingCallLogsQuickInsights,
    SearchableDropdown,
    AudioPlayer
  },
  props: {
    savedCallLogId: {
      type: String,
      default: ''
    },
    queryParams: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      audioInfo: null,
      selectTabGrouping: '',
      exportQuickInsightsTrigger: 0,
      disputeCallStatus: {},
      quickFilterInsightsLoader: false,
      showCallLogActionLoaderStatus: {},
      showBulkActionLoader: false,
      pendingBulkAdjustmentByLogs: false,
      triggerBulkActionHide: 0,
      callLogActionFormVisibilityStatus: {},
      showAudioPlayerForCall: {},
      openGridColumnToolPanel: false,
      openTourCallDetails: false,
      quickFilterData: {
        allRecords: [],
        totalsRecord: [],
        partialResult: false
      },
      tourCallbacks: {
        onNextStep: this.onNextStepHandler,
        onPreviousStep: this.onPreviousStepHandler,
        onStart: this.onStart
      },
      selectedQuickFilterGroupByColumn: {
        colId: 'campaignName',
        field: 'campaignName',
        headerName: 'Campaign',
        resizable: true,
        sortable: true,
        enableFilter: true,
        filterGroupName: 'Name/ID'
      },
      histogramActions: [],
      timeGroupDropdownOptions: {
        options: timeGroupDropdownOptions,
        reduce(option) {
          return option.code;
        },
        getOptionLabel: (option) => {
          if (option.code === 'auto') {
            return option.label + `  (${timeGroupDropdownOptions.find(findOption => findOption.code === this.histogramTimeGroupInterval).label})`;
          }


          return option.label;
        },
        clearable: false
      }
    };
  },
  computed: {
    ...mapState({
      campaigns: 'completedCampaignsWithoutStats',
    }),
    ...mapState('reporting', {
      recentCallTags: state => state.callLog.recentCallTags,
      sectionsVisibility: state => state.callLog.sectionsVisibility,
      showExportLoader: state => state.callLog.showExportLoader,
      callLogsColDefsLoadedFromAPI: state => state.callLog.colDefsLoadedFromAPI,
      insightsColDefsLoadedFromAPI: state => state.insight.colDefsLoadedFromAPI
    }),
    ...mapGetters([
      'isAdmin',
      'isBuyer',
      'isReadOnlyReportUser',
      'inAdminReportViewGroup'
    ]),
    ...mapGetters('reporting', [
      'role',
      'activeCallLog',
      'activeCallLogTranslatedTimeZoneId',
      'activeCallLogData',
      'histogramTimeGroupInterval',
      'callLogColumns',
      'insightColumnDefinitionsWithoutTags',
      'insightColumnDefLookup',
      'availableGroupBys',
      'activeCallLogTimeRange',
      'canDisputeConversions',
      'canExportReport',
      'availableCallLogsFilters',
      'activeCallLogFilterCriteria'
    ]),
    ...mapGetters('blockedNumbers', [
      'blockedNumbersHashMap'
    ]),
    isReportingAdmin(){
      return this.isAdmin || this.inAdminReportViewGroup;
    },
    hideTags() {
      return this.role === 'buyer';
    },
    histogramData() {
      return this.activeCallLogData.histogram;
    },
    steps(){
      return [...getCallLogsTourSteps(this.role)];
    },
    showHistogramLoader() {
      return this.activeCallLogData.showHistogramLoader;
    },
    groupTimeInterval() {
      return this.activeCallLog.settings.histogramSettings.groupTimeInterval;
    },
    gridState() {
      return this.activeCallLog.settings.gridSettings;
    },
    refreshGridData() {
      return this.activeCallLogData.refreshGridData;
    },
    callLogDetailsMapping() {
      return this.activeCallLogData.callLogDetails;
    },
    quickInsightsColumns() {
      const allColumns = this.insightColumnDefinitionsWithoutTags.map(column => {
        return {
          ...column,
          allowedAggFuncs: undefined,
          aggFunc: undefined,
          headerTooltip: column.headerTooltip ? column.headerTooltip : column.headerName,
          resizable: true
        };
      });

      allColumns.splice(0, 0, {
        ...this.selectedQuickFilterGroupByColumn,
        resizable: true
      });

      return allColumns;
    },
    quickFilterInsightsColumnState() {
      let pinnedColumnWidth = 140;

      if (this.activeCallLog?.settings?.quickInsightsSettings?.columnState?.length) {
        pinnedColumnWidth = this.activeCallLog?.settings?.quickInsightsSettings?.columnState[0].width;
      }

      const columnsState = [...insightsWithoutGroupingColumnState];

      columnsState.splice(0, 0, {
        colId: this.selectedQuickFilterGroupByColumn.colId,
        hide: false,
        pinned: 'left',
        lockPinned: true,
        width: pinnedColumnWidth,
        flex: null
      });

      return columnsState.map(state => {
        return {
          ...state,
          aggFunc: undefined
        };
      });
    },

    quickFilterInsightsTabs() {
      return quickFilterInsightsTabsColumnIds.map(colId => {
        const foundColumn = this.insightColumnDefLookup[colId];

        return {
          displayName: foundColumn?.headerName || '',
          filterGroupName: foundColumn?.filterGroupName,
          type: foundColumn?.type,
          enableFilter: foundColumn?.enableFilter,
          hide: !foundColumn,
          colId,
          valueFormatter: foundColumn?.valueFormatter
        };
      });
    },

    tagGroupBys() {
      return {
        tags: this.availableGroupBys.tags,
        columns: []
      };
    },

    timeGroupBy: {
      get() {
        return this.groupTimeInterval;
      },
      set(value) {
        const [startTime, endTime] = this.activeCallLogTimeRange;
        const diff = moment.duration(endTime.diff(startTime));
        const timeIntervalGroup = findTimeInterval(value);

        if (!timeIntervalGroup || (timeIntervalGroup && diff.as(timeIntervalGroup) < HISTOGRAM_POINTS_LIMIT)) {
          this.handleTimeGroupChange(value);
        } else {
          utilities.showToaster('error', 'Please reduce the time frame for this level of time aggregation.');
        }
      }
    },

    quickInsightsTabGrouping() {
      return this.selectTabGrouping || this.activeCallLog?.settings?.quickInsightsSettings?.selectTabGrouping;
    },

    disableQuickInsightsExport() {
      return !this.quickFilterData.allRecords.length;
    }
  },

  watch: {
    activeCallLog: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue.id == this.savedCallLogId) {
          this.handleCallLogsRefresh();
        }
      }
    },

    availableCallLogsFilters(newValue) {
      if (!newValue) {
        return;
      }

      this.setSettingsAndGroupingFromQueryParams(this.queryParams, newValue);
    }
  },

  beforeMount() {
    this.getCallLogsSectionVisibility();

    ngVueEventBus.$on(EVENT_STOP_PLAYER, this.hideCallRecording);
    ngVueEventBus.$on(EVENT_START_PLAYER, this.showCallRecording);
  },

  async mounted () {
    await this.getReportingTimezoneId({ reportType: CALLLOG_REPORT_TYPE, reportId: this.savedCallLogId });
    this.getCallLogsColDefs();
    this.getInsightsColDefs();
    this.getRecentlyAddedCallTags();
    if (this.isReportingAdmin) {
      this.getBlockedNumbers();
    }

    if (this.savedCallLogId && this.savedCallLogId !== 'unsaved') {
      await this.getInsight(this.savedCallLogId);
    } else {
      this.setDefaultCallLogActive();
      this.getCallLogDefaultGridStateToUserSettings(isMobile);
      this.getCallSummaryDefaultState(isMobile);
    }

    // Timezone ID getters changes in the next tick
    // Way to reproduce issue:
    // 1. Go to saved report
    // 2. Refresh page
    // 3. Go to default report
    // 4. Get tags API call doesn't reflect the right time zone
    // Doesn't work with nextTick as well
    setTimeout(() => {
      try {
        this.getTags(CALLLOG_REPORT_TYPE);
      } catch(e) {
        console.error(e);
      }
    }, 0);

    this.getCompletedCampaignsWithNoStats();

    this.loadTourSetting();
  },

  async created () {
    this.setCurrentView(CALLLOG_REPORT_TYPE);

    // Non-reactive object
    this.quickFilterInsightsGridState = {
      visibleColumns: [],
    };
    await this.getGroupsForUser();
  },

  methods: {
    ...mapActions('reporting', [
      'getCallLogsColDefs',
      'getReportingTimezoneId',
      'setDefaultCallLogActive',
      'setHistogramTimeGroup',
      'getCallLogHistogramData',
      'setDateRange',
      'getInsight',
      'setReportGridState',
      'refreshCallLogGridData',
      'updateCallLogLastUpdatedTime',
      'annotateTagToCalls',
      'callPaymentAdjustment',
      'multipleCallPaymentAdjustment',
      'multipleCallPaymentAdjustmentByLogs',
      'transcribeMultipleCalls',
      'transcribeMultipleCallsByLogs',
      'requestConversionAdjustment',
      'requestMultipleConversionAdjustment',
      'getRecentlyAddedCallTags',
      'getCallLogDetails',
      'prependFilterCriteriaToCallLogs',
      'getTags',
      'getInsightsColDefs',
      'getQuickFilterInsightsDataForCallLogs',
      'getCallLogsSectionVisibility',
      'setCallLogsSectionVisibility',
      'getCallLogDefaultGridStateToUserSettings',
      'saveCallLogDefaultGridStateToUserSettings',
      'getCallSummaryDefaultState',
      'saveCallSummaryDefaultStateToUserSettings',
      'setDontShowCallLogsTourAgain',
      'resetAutoCompleteValues',
      'resetCallLogColumnState',
      'exportCallLog',
      'resetFilterCriteria',
      'setCallLogQuickInsightsTab',
      'resetCallLogDetails',
      'setCallLogExportLoaderVisibility',
      'setCallSummaryColumnState',
      'setCurrentView'
    ]),
    ...mapActions('blockedNumbers', [
      'blockNumbers',
      'getBlockedNumbers',
      'unblockNumber'
    ]),

    ...mapActions([
      'getCompletedCampaignsWithNoStats',
      'getGroupsForUser'
    ]),

    onQuickInsightsTabChange({groupingColId, isMobile}) {
      this.setCallLogQuickInsightsTab(groupingColId);

      if (isDefaultReportId(this.savedCallLogId)) {
        this.saveCallSummaryDefaultStateToUserSettings({ selectTabGrouping: groupingColId, isMobile });
      }
    },

    exportQuickInsightsReport() {
      this.exportQuickInsightsTrigger++;
    },

    loadTourSetting() {
      userSettingsService.getUserSettings().then(settings => {
        if (settings.showCallLogsTour !== 'no') {
          this.$tours['callLogsTour'].start();
        }
      });
    },

    userTriggerStartTour() {
      this.$tours['callLogsTour'].start();
    },

    scrollTourIntoView() {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 400);
    },

    onNextStepHandler(stepIndex) {
      const nextStep = this.steps[stepIndex + 1];
      if (nextStep) {
        if (nextStep.scrollsToTop) {
          this.scrollTourIntoView();
        }

        if (nextStep.expandsCustomizeColumn) {
          this.openGridColumnToolPanel = true;

          setTimeout(() => {
            this.openGridColumnToolPanel = false;
          }, 500);
        }

        if (nextStep.expandsCallDetail) {
          setTimeout(() => {
            this.openTourCallDetails = true;
          }, 500);
        }
      }

      const currentStep = this.steps[stepIndex];
      if (currentStep && currentStep.expandsCallDetail) {
        setTimeout(() => {
          this.openTourCallDetails = false;
        }, 500);
      }
    },

    onStart() {
      this.scrollTourIntoView();
    },

    onPreviousStepHandler(stepIndex) {
      const previousStep = this.steps[stepIndex - 1];
      if (previousStep) {
        if (previousStep.scrollsToTop) {
          this.scrollTourIntoView();
        }

        if (previousStep.expandsCustomizeColumn) {
          this.openGridColumnToolPanel = true;

          setTimeout(() => {
            this.openGridColumnToolPanel = false;
            window.scroll(0, document.documentElement.offsetHeight);
          }, 500);
        }

        if (previousStep.expandsCallDetail) {
          setTimeout(() => {
            this.openTourCallDetails = true;
          }, 500);
        }
      }

      const currentStep = this.steps[stepIndex];
      if (currentStep && currentStep.expandsCallDetail) {
        setTimeout(() => {
          this.openTourCallDetails = false;
        }, 500);
      }
    },

    dontShowTourAgain() {
      this.setDontShowCallLogsTourAgain();
      this.$tours['callLogsTour'].finish();
    },

    setSettingsAndGroupingFromQueryParams(queryParams, availableCallLogsFilters) {
      if (!queryParams?.filterKeys
          || !queryParams?.filterValues
          || !availableCallLogsFilters) {
        return;
      }

      this.resetFilterCriteria(CALLLOG_REPORT_TYPE);

      const buyerTargetNameIndex = queryParams.filterKeys.findIndex(k => k === 'buyerTargetName');
      if (buyerTargetNameIndex > -1 && this.role !== 'buyer') {
        queryParams.filterKeys[buyerTargetNameIndex] = 'targetName';
      }

      const filters = getFiltersFromKeys(queryParams.filterKeys, queryParams.filterValues, availableCallLogsFilters, this.activeCallLogFilterCriteria);
      const filterChanged = filters.length > 0;
      filters.forEach(filter => {
        this.prependFilterCriteriaToCallLogs(filter);
      });

      this.selectTabGrouping = queryParams.isGroup ? queryParams.filterKeys?.[0] : '';

      // Refresh grid only if filters have changed and saved report loaded
      if (filterChanged && (!this.savedCallLogId || this.savedCallLogId === this.activeCallLog.id)) {
        this.handleCallLogsRefresh();
      }

      if (this.queryParams.reportStart && this.queryParams.reportEnd) {

        const { reportStart, reportEnd } = this.queryParams;

        const endTime = moment.utc(reportEnd,  moment.ISO_8601, true).tz(this.activeCallLogTranslatedTimeZoneId);
        const startTime = moment.utc(reportStart,  moment.ISO_8601, true).tz(this.activeCallLogTranslatedTimeZoneId);

        if (startTime.isValid() && endTime.isValid()) {
          this.setDateRange({
            dateRangeDetails: {
              isCustomRange: true,
              timeRange: [
                startTime,
                endTime
              ]
            },
            reportType: CALLLOG_REPORT_TYPE
          });
        }
      }


    },

    toggleSection(section) {
      this.setCallLogsSectionVisibility({ section, visible: !this.sectionsVisibility[section] });

      if (this.sectionsVisibility[section]) {
        // TODO: Reload only the respective section's data
        this.fetchData();
      }
    },

    handleFilterByColumn(columns) {
      columns.forEach(column => {
        this.prependFilterCriteriaToCallLogs(column);
      });

      this.handleCallLogsRefresh();
    },

    handleCallLogsRefresh() {
      if (!this.activeCallLogTranslatedTimeZoneId) {
        this.getReportingTimezoneId({reportType: CALLLOG_REPORT_TYPE, reportId: this.savedCallLogId}).then(() => {
          this.fetchData();
        });
      } else {
        this.fetchData();
      }

      this.showCallLogActionLoaderStatus = {};
      this.callLogActionFormVisibilityStatus = {};
      this.showAudioPlayerForCall = {};
    },

    async fetchData() {
      this.refreshCallLogGridData();
      this.resetCallLogDetails();

      if (this.sectionsVisibility['timeline']) {
        try {
          await this.getCallLogHistogramData();
        } catch(e) {
          utilities.errorHandler(e);
        }
      }

    },

    handleTimeGroupChange(timeGroup) {
      this.setHistogramTimeGroup(timeGroup);

      this.handleCallLogsRefresh();
    },

    handleFilterByDate({ reportStartInEpoch, reportEndInEpoch, timeGroupInterval }) {
      const endTime = moment.utc(reportEndInEpoch).tz(this.activeCallLogTranslatedTimeZoneId).endOf(timeGroupInterval);
      const startTime = moment.utc(reportStartInEpoch).tz(this.activeCallLogTranslatedTimeZoneId).startOf(timeGroupInterval);

      this.setDateRange({
        dateRangeDetails: {
          isCustomRange: true,
          timeRange: [
            startTime,
            endTime
          ]
        },
        reportType: CALLLOG_REPORT_TYPE
      });

      this.handleCallLogsRefresh();
    },

    handleChartRangeSelection({ reportStartInEpoch, reportEndInEpoch, timeGroupInterval }) {
      this.setHistogramActions();
      this.handleFilterByDate({ reportStartInEpoch, reportEndInEpoch, timeGroupInterval });
    },

    handleDateRangeChange() {
      this.histogramActions = [];
      this.getTags(CALLLOG_REPORT_TYPE);
      this.resetAutoCompleteValues(CALLLOG_REPORT_TYPE);
    },

    showAudioPlayer({ recordingUrl, inboundNumber, callId, hasTranscription }) {
      ngVueEventBus.$emit(EVENT_STOP_PLAYER);
      this.$refs.audioPlayer?.resetPlayer();
      
      this.audioInfo = {
        recordingUrl: recordingUrl,
        inboundNumber: inboundNumber,
        callId: callId,
        hasTranscription: hasTranscription
      };
      ngVueEventBus.$emit(EVENT_START_PLAYER, callId);
    },

    stopAudioPlayer() {
      ngVueEventBus.$emit(EVENT_STOP_PLAYER);
      this.$refs.audioPlayer.hidePlayer();
    },

    hideCallRecording() {
      this.showAudioPlayerForCall = {};
    },

    showCallRecording(callId) {
      Vue.set(this.showAudioPlayerForCall, callId, true);
    },

    onGridStateChange({ gridState, isMobile }) {
      this.setReportGridState({ reportType: CALLLOG_REPORT_TYPE, state: gridState });

      if (isDefaultReportId(this.savedCallLogId)) {
        this.saveCallLogDefaultGridStateToUserSettings({ gridState, isMobile });
      }
    },

    setHistogramActions() {
      this.histogramActions.push({
        startTime: this.activeCallLogTimeRange[0],
        endTime: this.activeCallLogTimeRange[1],
        timeGroupInterval: findTimeInterval(this.histogramTimeGroupInterval)
      });
    },

    zoomOutHistogram() {
      const lastElement = this.histogramActions.pop();

      this.setDateRange({
        dateRangeDetails: {
          isCustomRange: true,
          timeRange: [
            lastElement.startTime,
            lastElement.endTime,
          ]
        },
        reportType: CALLLOG_REPORT_TYPE
      });

      this.handleCallLogsRefresh();
    },

    async onBlockNumber({ number, callId }) {
      Vue.set(this.showCallLogActionLoaderStatus, callId, true);
      Vue.set(this.callLogActionFormVisibilityStatus, callId, true);

      try {
        await this.blockNumbers([number]);
        Vue.set(this.callLogActionFormVisibilityStatus, callId, false);
        this.resetCallLogDetails();
        utilities.showToaster('success', this.$i18n.t('BLOCK_NUMBER_SUCCESS'));
      } catch(e) {
        utilities.errorHandler(e);
      } finally {
        Vue.set(this.showCallLogActionLoaderStatus, callId, false);
      }
    },

    async blockMultipleNumbers(numbers) {
      try {
        this.showBulkActionLoader = true;
        await this.blockNumbers(numbers);
        this.resetCallLogDetails();
        utilities.showToaster('success', 'Successfully blocked numbers');
        this.triggerBulkActionHide++;
      } catch(e) {
        utilities.errorHandler(e);
      } finally {
        this.showBulkActionLoader = false;
      }
    },

    async onUnblockNumber(blockedNumberDetails) {
      try {
        await this.unblockNumber(blockedNumberDetails.id);
        this.resetCallLogDetails();
        utilities.showToaster('success', this.$i18n.t('UNBLOCK_NUMBER_SUCCESS'));
      } catch (e) {
        utilities.errorHandler(e);
      }
    },

    async annotateCall({ tag, callId }) {
      Vue.set(this.showCallLogActionLoaderStatus, callId, true);
      Vue.set(this.callLogActionFormVisibilityStatus, callId, true);

      try {
        await this.annotateTagToCalls({ tag, callIds: [callId] });
        Vue.set(this.callLogActionFormVisibilityStatus, callId, false);
        utilities.showToaster('success', this.$i18n.t('ADD_ANNOTATION_SUCCESS'));
        this.getRecentlyAddedCallTags();
      } catch(e) {
        utilities.errorHandler(e);
      } finally {
        Vue.set(this.showCallLogActionLoaderStatus, callId, false);
      }
    },

    async annotateMultipleCalls({ tag, callIds }) {
      try {
        this.showBulkActionLoader = true;
        await this.annotateTagToCalls({ tag, callIds });
        utilities.showToaster('success', this.$i18n.t('ADD_ANNOTATION_MULTIPLE_SUCCESS'));
        this.triggerBulkActionHide++;
      } catch(e) {
        utilities.errorHandler(e);
      } finally {
        this.showBulkActionLoader = false;
      }
    },

    async callPaymentDispute({ data, callId }) {
      Vue.set(this.showCallLogActionLoaderStatus, callId, true);
      Vue.set(this.callLogActionFormVisibilityStatus, callId, true);

      if (this.isReportingAdmin) {
        await this.adjustCallPayment({ data, callId });
      } else {
        await this.requestConversion({data, callId});
      }
    },

    async multipleCallPaymentDispute({ callIds, data }) {
      if (this.isReportingAdmin) {
        await this.adjustMultipleCallsPayment({ callIds, data });
        this.triggerBulkActionHide++;
      } else {
        // Buyers don't have access to bulk actions at this time. If that is ever implemented, uncomment and test this before letting it go live
        //await this.requestConversionMultiple({data, callIds});
      }
    },

    async multipleCallPaymentDisputeByLogs({ callLogs, data }) {
      if (this.isReportingAdmin) {
        await this.adjustMultipleCallsPaymentByLogs({ callLogs, data });
      } else {
        // Buyers don't have access to bulk actions at this time. If that is ever implemented, uncomment and test this before letting it go live
        //await this.requestConversionMultiple({data, callIds});
      }
    },

    async multipleCallTranscription({ callIds }) {
      if (this.isReportingAdmin) {
        this.showBulkActionLoader = true;
        try {
          await this.transcribeMultipleCalls({ callIds });
          utilities.showToaster('success', this.$i18n.t('TRANSCRIBE_MULTIPLE_SUCCESS'));
          this.triggerBulkActionHide++;
        } catch (error) {
          utilities.errorHandler(error);
        } finally {
          this.showBulkActionLoader = false;
        }
      }
    },

    async multipleCallTranscriptionByLogs({ callLog }) {
      if (this.isReportingAdmin) {
        this.showBulkActionLoader = true;
        try {
          await this.transcribeMultipleCallsByLogs({ callLog });
          utilities.showToaster('success', this.$i18n.t('TRANSCRIBE_BY_LOGS_SUCCESS'));
          this.triggerBulkActionHide++;
        } catch (error) {
          utilities.errorHandler(error);
        } finally {
          this.showBulkActionLoader = false;
        }
      }
    },

    async requestConversion({ data, callId }) {
      try {
        await this.requestConversionAdjustment({ data, callId });
        Vue.set(this.callLogActionFormVisibilityStatus, callId, false);
        utilities.showToaster('success', this.$i18n.t('REQUEST_CONVERSION_ADJUSTMENT_SUCCESS'));
        this.disputeCallStatus = {
          [callId]: 'requested'
        };
      } catch (error) {
        utilities.errorHandler(error);
      } finally {
        Vue.set(this.showCallLogActionLoaderStatus, callId, false);
      }
    },

    async requestConversionMultiple({ data, callIds }) {
      try {
        await this.requestMultipleConversionAdjustment({ data, callIds });
        utilities.showToaster('success', this.$i18n.t('REQUEST_CONVERSION_ADJUSTMENT_MULTIPLE_SUCCESS'));
      } catch (error) {
        utilities.errorHandler(error);
      }
    },

    async adjustCallPayment({ data, callId }) {
      try {
        await this.callPaymentAdjustment({ data, callId });
        Vue.set(this.callLogActionFormVisibilityStatus, callId, false);
        utilities.showToaster('success', this.$i18n.t('PAYOUT_ADJUSTMENT_SUCCESS'));
      } catch (error) {
        utilities.errorHandler(error);
      } finally {
        Vue.set(this.showCallLogActionLoaderStatus, callId, false);
      }
    },

    async adjustMultipleCallsPayment({ callIds, data }) {
      try {
        await this.multipleCallPaymentAdjustment({ callIds, data });
        utilities.showToaster('success', this.$i18n.t('PAYOUT_ADJUSTMENT_MULTIPLE_SUCCESS'));
      } catch (error) {
        utilities.errorHandler(error);
      }
    },

    async adjustMultipleCallsPaymentByLogs({ callLogs, data }) {
      this.pendingBulkAdjustmentByLogs = true;
      let adjustError;
      try {
        await this.multipleCallPaymentAdjustmentByLogs({ callLogs, data });
      } catch (error) {
        adjustError = error;
      } finally {
        setTimeout(() => {
          if (adjustError) {
            utilities.errorHandler(adjustError);
          } else {
            utilities.showToaster('success', this.$i18n.t('PAYOUT_ADJUSTMENT_BY_LOGS_SUCCESS'));
          }
          this.pendingBulkAdjustmentByLogs = false;
          this.triggerBulkActionHide++;
        }, 1800);
      }
    },

    async getCallDetailsFromAPI(callId) {
      try {
        await this.getCallLogDetails(callId);
      } catch (error) {
        utilities.errorHandler(error);
      }
    },

    onGridDataLoaded() {
      this.updateCallLogLastUpdatedTime();
    },

    setCallLogActionFormVisibilityStatus({ status, callId }) {
      Vue.set(this.callLogActionFormVisibilityStatus, callId, status);
    },

    async loadQuickFilterData() {
      if (!this.activeCallLogTranslatedTimeZoneId) {
        return;
      }

      try {
        this.quickFilterInsightsLoader = true;
        const { allRecords, totalsRecord, partialResult } = await this.getQuickFilterInsightsDataForCallLogs({
          groupBy: this.selectedQuickFilterGroupByColumn,
          columns: this.quickFilterInsightsGridState.visibleColumns,
        });

        this.quickFilterData = Object.freeze({
          allRecords,
          totalsRecord,
          partialResult,
        });

      } catch (error) {
        console.error(error);
        utilities.errorHandler(error);
      } finally {
        this.quickFilterInsightsLoader = false;
      }
    },

    loadQuickFilterInsightsData({ groupingColumn, columns }) {
      if (this.selectedQuickFilterGroupByColumn.colId !== groupingColumn.colId) {

        this.quickFilterInsightsGridState = {
          visibleColumns: columns.filter(c => c.colId !== this.selectedQuickFilterGroupByColumn.colId)
        };
        this.selectedQuickFilterGroupByColumn = {
          ...groupingColumn,
        };
      } else {
        this.quickFilterInsightsGridState = {
          visibleColumns: columns
        };
      }

      this.resetQuickFilterData();

      setTimeout(() => {
        this.loadQuickFilterData();
      }, 0);
    },

    resetQuickFilterData() {
      this.quickFilterData = {
        allRecords: [],
        totalsRecord: []
      };
    },

    onColumnReset() {
      this.resetCallLogColumnState();
    },

    async handleReportExport() {
      this.setCallLogExportLoaderVisibility(true);

      try {
        await this.exportCallLog();

      } catch(error) {
        console.error(error);
        utilities.errorHandler(error);
      } finally {
        this.setCallLogExportLoaderVisibility(false);
      }
    },

    async handleExportReportJSONRequestBody() {
      this.setCallLogExportLoaderVisibility(true);
      try {
        const requestBody = await this.exportCallLog(false);
        utilities.downloadContentAsFile({
          contentAsString: JSON.stringify(requestBody, null, 2),
          mimeType: 'application/json',
          fileName: 'requestBody.json'
        });
      } catch(error) {
        console.error(error);
        utilities.errorHandler(error);
      } finally {
        this.setCallLogExportLoaderVisibility(false);
      }
    },

    onCallLogSummaryGridStateChange({ columnState, isMobile }) {
      this.setCallSummaryColumnState(columnState);

      if (isDefaultReportId(this.savedCallLogId)) {
        this.saveCallSummaryDefaultStateToUserSettings({ columnState, selectTabGrouping: this.quickInsightsTabGrouping, isMobile });
      }
    },

    hidePlayer() {
      this.audioInfo = null;
      ngVueEventBus.$emit(EVENT_STOP_PLAYER);
    },
    viewTranscription() {
      if (this.audioInfo) {
        this.$refs.reportingCallLogsGrid.showTranscriptionDetails(this.audioInfo.callId);
      }
    }
  },
  i18n: {
    locale: 'en',
    sharedMessages,
    messages
  }
};
</script>

<style lang="scss" scoped>
.reporting-main-container--call-logs {
  background: var(--component-call-logs__container-bg-color);
  margin: -10px;
  padding: 10px;
}

.reporting-call-logs-data {
  margin-bottom: 15px;
}
</style>

<style lang="scss">
.reporting-call-logs-header {
  display: flex;
  align-items: center;
  padding-bottom: 5px;
  margin-bottom: 10px;
  border-bottom: 1px solid var(--component-form__input-bg-default);
}

.reporting-call-logs-header__title {
  font-size: 15px;
  line-height: 2;
  letter-spacing: 1px;
  align-self: flex-end;
  color: var(--component-reporting-header__title-color);
}

.reporting-call-logs-header__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;

  button:not(.btn-primary) {
    color: var(--component-reporting-settings-btn-font-color);

    &:hover:not(:disabled) {
      color: var(--component-reporting-settings-btn-font-hover-color)
    }
  }
}

@media (max-width: $screen-xs-max) {
  .reporting-call-logs-header__right--overflow {
    > button, > .dropdown {
      margin-top: 10px;
    }
  }
}
</style>